import { useState, type FormEvent } from 'react';
import type { WithholdingReportResponse } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import type { CsvCell } from './csv';
import { formatMoney } from './money';

interface Props {
  companyId: string;
  currency: string;
  defaultFrom: string;
  defaultTo: string;
  onSessionLost: () => void;
}

/**
 * الخصم من المنبع لحسابنا فى فترة — من سطور حساب ضرائب الخصم فى الدفتر نفسه، للتسوية مع
 * المصلحة بإجمالى لكل عميل. لا يُحسب هنا شىء غير ما رُحّل.
 */
export default function WithholdingReport({
  companyId,
  currency,
  defaultFrom,
  defaultTo,
  onSessionLost,
}: Props) {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [report, setReport] = useState<WithholdingReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function load(event: FormEvent): Promise<void> {
    event.preventDefault();
    setLoading(true);
    setFailure(null);
    try {
      setReport(await api.withholdingReport(companyId, from, to));
    } catch (caught) {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      setFailure(
        caught instanceof ProblemError
          ? (caught.problem.detail ?? caught.problem.title)
          : caught instanceof Error
            ? caught.message
            : String(caught),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="panel">
      <h2>الخصم من المنبع لحسابنا</h2>
      <form className="form-row" onSubmit={(event) => void load(event)}>
        <label>
          من
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          إلى
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
        <div className="form-actions">
          <button type="submit" className="ghost" disabled={loading || !from || !to}>
            {loading ? 'جارى العرض…' : 'عرض'}
          </button>
        </div>
      </form>
      {failure && <p className="error">{failure}</p>}
      {report && <WithholdingReceivableView report={report} currency={currency} />}
    </div>
  );
}

/** جسم التقرير — مشترك بين شاشة التحصيل وتبويب الخصم من المنبع فى التقارير. */
export function WithholdingReceivableView({
  report,
  currency,
}: {
  report: WithholdingReportResponse;
  currency: string;
}) {
  return (
    <>
      <p className="sub">
        حساب {report.accountCode} • افتتاحى {formatMoney(report.openingBalance)} • خُصم فى الفترة{' '}
        {formatMoney(report.totalDebit)} • سُوّى {formatMoney(report.totalCredit)} • الرصيد لدى
        المصلحة {formatMoney(report.closingBalance)} {currency}
      </p>
      {report.byCustomer.length === 0 ? (
        <p className="sub">لا خصم من المنبع فى هذه الفترة.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>العميل</th>
                <th>خُصم</th>
                <th>سُوّى</th>
              </tr>
            </thead>
            <tbody>
              {report.byCustomer.map((row) => (
                <tr key={row.customerId ?? 'none'}>
                  <td>{row.nameAr ? row.code + ' — ' + row.nameAr : 'بلا عميل (قيد يدوى)'}</td>
                  <td className="num-cell">{formatMoney(row.debit)}</td>
                  <td className="num-cell">{formatMoney(row.credit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export const WITHHOLDING_HEADER = ['العميل', 'خُصم', 'سُوّى'];

export function withholdingRows(report: WithholdingReportResponse): CsvCell[][] {
  return [
    ...report.byCustomer.map((row) => [
      row.nameAr ? row.code + ' — ' + row.nameAr : 'بلا عميل (قيد يدوى)',
      row.debit,
      row.credit,
    ]),
    ['الإجمالى', report.totalDebit, report.totalCredit],
    ['افتتاحى', report.openingBalance, ''],
    ['الرصيد لدى المصلحة', report.closingBalance, ''],
  ];
}
